/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'ISP | Антипаттерны и запахи',
  description: 'Антипаттерны и запахи кода, которые мешают следовать принципу разделения интерфейса'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      section: "section",
      h1: "h1",
      p: "p",
      a: "a",
      h2: "h2",
      pre: "pre",
      code: "code",
      em: "em",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_components.section, {
      children: [_jsx(_components.h1, {
        children: "Антипаттерны и запахи"
      }), _jsxs(_components.p, {
        children: ["Принцип разделения интерфейса концептуально похож на ", _jsx(_components.a, {
          href: "/srp",
          children: "принцип единственной ответственности"
        }), " и решает схожие проблемы, только относящиеся к более высокому уровню абстракции."]
      }), _jsx(_components.p, {
        children: "Два самых распространённых «запаха», намекающих, что нарушен ISP, — это грязный интерфейс и «пустая» реализация."
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Грязный интерфейс"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://medium.com/@severinperez/avoiding-interface-pollution-with-the-interface-segregation-principle-5d3859c21013",
            children: "Проблема грязного интерфейса"
          }), " возникает, когда интерфейс содержит в себе слишком много методов и полей. Как и большие модули в SRP, грязный интерфейс в ISP приводит к плохой читаемости и дорогой поддержке."]
        }), _jsx(_components.p, {
          children: "Рассмотрим проблему на примере. Допустим, у нас есть калькулятор, который умеет складывать, вычитать, умножать, делить и брать квадратные корни."
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "interface Calculator {\n  add(a: number, b: number): number\n  subtract(a: number, b: number): number\n  multiply(a: number, b: number): number\n  divide(a: number, b: number): number\n  sqrt(a: number): number\n}\n\nclass BasicCalculator implements Calculator {\n  // ...\n}\n"
          })
        }), _jsx(_components.p, {
          children: "При расширении функциональности интерфейс может стать слишком большим:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "interface Calculator {\n  add(a: number, b: number): number\n  subtract(a: number, b: number): number\n  multiply(a: number, b: number): number\n  divide(a: number, b: number): number\n  sqrt(a: number): number\n  power(base: number, power: number): number\n  sin(x: number): number\n  cos(x: number): number\n  tan(x: number): number\n  log(x: number): number\n  log10(x: number): number\n  // ...\n}\n"
          })
        }), _jsx(_components.p, {
          children: "Класс, реализующий такой интерфейс будет неоправданно большим, из-за чего сложность понимания кода резко вырастет."
        }), _jsxs(_components.p, {
          children: ["Кроме высокой сложности грязные интерфейсы приводят и ещё к одной проблеме, которую мы упоминали в ", _jsx(_components.a, {
            href: "/isp/in-ideal-world",
            children: "разделе"
          }), " с примерами из идеального мира — пустой реализации."]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "«Пустая» реализация"
        }), _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://stackoverflow.com/questions/20861107/can-anyone-provide-an-example-of-the-liskov-substitution-principle-lsp-using-v",
            children: "«Пустая» реализация интерфейса"
          }), " возникает, когда появляется модуль, которому не нужны ", _jsx(_components.em, {
            children: "все"
          }), " методы из реализуемого интерфейса."]
        }), _jsx(_components.p, {
          children: "Попробуем создать арифметический калькулятор. Ему не нужны тригонометрические функции и логарифмы, но из-за того, что они описаны в интерфейсе, нам придётся поставить заглушки на эти методы:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class ArithmeticCalculator implements Calculator {\n  // функциональность методов, которые нужны, описываем полностью\n  add(a: number, b: number): number {\n    return a + b\n  }\n\n  subtract(a: number, b: number): number {\n    return a - b\n  }\n\n  multiply(a: number, b: number): number {\n    return a * b\n  }\n\n  divide(a: number, b: number): number {\n    if (b === 0) {/*...*/}\n    return a / b\n  }\n\n  // остальные методы не нужны;\n  // но интерфейс Calculator требует их реализации,\n  // приходится писать заглушки\n  sqrt(a: number): number\n  power(base: number, power: number): number\n  sin(x: number): number { return 0 }\n  cos(x: number): number { return 0 }\n  tan(x: number): number { return 0 }\n  log(x: number): number { return 0 }\n  log10(x: number): number { return 0 }\n  // ...\n}\n"
          })
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "ISP решает"
        }), _jsx(_components.p, {
          children: "ISP помогает проектировать интерфейсы, избегая проблем выше. На примере калькулятора мы бы могли выделить несколько интерфейсов с соответствующими ролями:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "interface ArithmeticCalculator {\n  add(a: number, b: number): number\n  subtract(a: number, b: number): number\n  multiply(a: number, b: number): number\n  divide(a: number, b: number): number\n}\n\ninterface ExponentCalculator {\n  sqrt(a: number): number\n  power(base: number, power: number): number\n}\n\ninterface TrigonometricCalculator {\n  sin(x: number): number\n  cos(x: number): number\n  tan(x: number): number\n}\n\ninterface LogarithmicCalculator {\n  log(x: number): number\n  log10(x: number): number\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Тогда арифметическому калькулятору достаточно было бы реализовать интерфейс ", _jsx(_components.code, {
            children: "ArithmeticCalculator"
          }), ". Более навороченный инженерный калькулятор смог бы реализовать несколько интерфейсов одновременно с помощью ", _jsx(_components.a, {
            href: "/isp/patterns",
            children: "множественного наследования"
          }), "."]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Материалы к разделу"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://medium.com/@severinperez/avoiding-interface-pollution-with-the-interface-segregation-principle-5d3859c21013",
              children: "Avoiding Interface Pollution with the ISP"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://martinfowler.com/bliki/RoleInterface.html",
              children: "RoleInterface, Martin Fowler"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://makedev.org/principles/solid/isp.html",
              children: "Принцип разделения интерфейса"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://stackoverflow.com/questions/20861107/can-anyone-provide-an-example-of-the-liskov-substitution-principle-lsp-using-v",
              children: "Can anyone provide an example of the LSP?"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/Null_object_(%D1%88%D0%B0%D0%B1%D0%BB%D0%BE%D0%BD_%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D1%8F)",
              children: "Шаблон Null-object"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://stackoverflow.com/questions/37355421/does-null-object-pattern-break-interface-segregation-principle",
              children: "Does Null-object Pattern Break ISP?"
            })
          }), "\n"]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Вопросы"
        }), _jsx(Quiz, {
          name: "isp-anti-1"
        }), _jsx(Quiz, {
          name: "isp-anti-2"
        }), _jsx(Quiz, {
          name: "isp-anti-3"
        })]
      })]
    });
  }
}
export default MDXContent;
